import React from 'react';
import PropTypes from 'prop-types';
import { Link, useLocation } from 'react-router-dom';
import { ArrowLeft } from 'tabler-icons-react';
import { Button, Skeleton } from '@mantine/core';
import AppStack from './AppStack';
import AppFlexbox from './AppFlexbox';
import AppText from './AppText';
import ActionableIcon from './ActionableIcon';
import { useVendorBackPath } from '../../helpers/vendorHelper';
import { useStoreBackPath } from '../../helpers/storeHelper';
import { useAdminBackPath } from '../../helpers/adminHelper';
import { useMediaQueryIndex } from '../../helpers/hooks';

const PageView = ({
  title,
  subTitle,
  backPath: backPathOverride,
  backPathDefault,
  onBackClick,
  width,
  rightSection,
  loading,
  badgeContent,
  children,
  formProps = {
    onSubmit: null,
    withFormControls: false,
    isSubmitting: false,
    hasUnsavedChanges: false,
    onDelete: null,
    deleteTitle: null
  },
  ...rest
}) => {
  const { isTabletOrSmaller, isLargeMobileOrSmaller } = useMediaQueryIndex();
  const { pathname } = useLocation();
  const { backPath } = backPathOverride
    ? { backPath: backPathOverride }
    : pathname.startsWith('/admin')
    ? useAdminBackPath(backPathDefault || '/admin')
    : pathname.startsWith('/vendor')
    ? useVendorBackPath(backPathDefault || '/vendor')
    : useStoreBackPath(backPathDefault || '/store');

  return (
    <AppStack
      component={formProps.onSubmit ? 'form' : undefined}
      onSubmit={formProps.onSubmit}
      p={{ base: 8, sm: '16px 16px 80px 16px' }}
      style={{
        flex: 1,
        margin: 'auto',
        width: '100%',
        maxWidth: width || 1050,
        paddingTop: 16,
        gap: 16,
        ...rest.style
      }}
      {...rest}
    >
      {loading ? (
        <AppFlexbox
          style={{
            alignItems: 'start',
            justifyContent: 'space-between',
            gap: 8
          }}
        >
          <AppFlexbox style={{ gap: 8 }}>
            <ActionableIcon
              color="dark"
              component={Link}
              radius="md"
              style={{ marginTop: 5 }}
              to={backPathOverride || backPath}
              variant="subtle"
            >
              <ArrowLeft />
            </ActionableIcon>
            <AppStack style={{ gap: 5 }}>
              <Skeleton height={36} width={200} />
              <Skeleton height={14} width={150} />
            </AppStack>
          </AppFlexbox>

          {!!rightSection && (
            <AppFlexbox style={{ gap: 5 }}>
              <Skeleton height={30} width={125} />
            </AppFlexbox>
          )}
        </AppFlexbox>
      ) : (
        <AppFlexbox
          style={{
            alignItems: 'start',
            justifyContent: 'space-between',
            flexDirection: isLargeMobileOrSmaller ? 'column' : 'row',
            gap: 8
          }}
        >
          <AppFlexbox style={{ gap: 8 }}>
            <ActionableIcon
              color="dark"
              component={Link}
              onClick={onBackClick}
              radius="md"
              style={{ marginTop: 5 }}
              to={backPathOverride || backPath}
              variant="subtle"
            >
              <ArrowLeft />
            </ActionableIcon>

            <AppStack style={{ gap: 8 }}>
              <AppStack style={{ gap: 0 }}>
                <AppFlexbox style={{ gap: 8, alignItems: 'center' }}>
                  <AppText
                    style={{
                      flex: 1,
                      fontSize: 24,
                      fontWeight: 700,
                      wordBreak: 'break-word'
                    }}
                  >
                    {title}
                  </AppText>
                </AppFlexbox>
                {subTitle && (
                  <AppText
                    style={{
                      fontSize: 12,
                      lineHeight: '16px',
                      color: '#666'
                    }}
                  >
                    {subTitle}
                  </AppText>
                )}
              </AppStack>

              {badgeContent}
            </AppStack>
          </AppFlexbox>

          {rightSection && (
            <AppFlexbox
              style={{
                alignItems: 'center',
                gap: 8,
                marginTop: 5,
                flex: isLargeMobileOrSmaller ? 1 : 'unset',
                alignSelf: 'stretch'
              }}
            >
              {rightSection}
            </AppFlexbox>
          )}
        </AppFlexbox>
      )}

      {children}

      {formProps.withFormControls && (
        <AppFlexbox
          p={{ base: 16, sm: '16px 0px' }}
          style={{
            justifyContent: 'flex-end',
            flexDirection: isLargeMobileOrSmaller ? 'column-reverse' : 'row'
          }}
        >
          {formProps.onDelete && (
            <Button
              color="red"
              disabled={formProps.isSubmitting}
              onClick={formProps.onDelete}
              radius="md"
              size={isTabletOrSmaller ? 'sm' : 'compact-sm'}
              type="button"
              variant="filled"
            >
              {formProps.deleteTitle || 'Delete'}
            </Button>
          )}
          <Button
            color="dark"
            disabled={!formProps.hasUnsavedChanges}
            id="save-button"
            loading={formProps.isSubmitting}
            radius="md"
            size={isTabletOrSmaller ? 'sm' : 'compact-sm'}
            type="submit"
            variant="filled"
          >
            Save
          </Button>
        </AppFlexbox>
      )}
    </AppStack>
  );
};

PageView.propTypes = {
  backPath: PropTypes.string,
  backPathDefault: PropTypes.string,
  badgeContent: PropTypes.node,
  children: PropTypes.node,
  formProps: PropTypes.object,
  loading: PropTypes.bool,
  onBackClick: PropTypes.func,
  onBackPath: PropTypes.func,
  rightSection: PropTypes.node,
  subTitle: PropTypes.string,
  title: PropTypes.string,
  width: PropTypes.number
};

export default PageView;
